<template>
  <BittsTooltip trigger="hover" v-bind="tooltipBinds">
    <BittsButton
      v-if="showAddToCrm"
      size="x-small"
      :text="buttonText"
      :aria-disabled="buttonDisabled"
      :left-icon="buttonIcon"
      :variant="variant"
      @click.prevent.stop="addToCRM"
    />
    <template #title>
      <p style="text-align: center"> You have this contact in your CRM </p>
      <BittsButton
        type="white"
        size="x-small"
        text="View in CRM"
        :right-icon="['fak', 'external-link']"
        style="width: 100%"
        class="mt-10"
        @click.prevent="navigateToContact"
        @click.middle="navigateToContact"
        @mousedown.middle.prevent.stop
      />
    </template>
  </BittsTooltip>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsButtonVariant,
  BittsIcon,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import axios from 'axios';
import { computed, inject, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useRouteViewTracker } from '@/components/sales/AccountDrawer/composables/useRouteViewTracker';

import useAuth from '@/composables/useAuth';
import { useDealNavActivities } from '@/composables/useDealNavActivities';
import useIteratively from '@/composables/useIteratively';
import { WRITE_ACCESS_ROLES } from '@/constants/team';
import { ProvidedHasSalesforceFeed } from '@/injectionKeys/account_drawer';
import { useCopilotContactsStore, useFlashesStore } from '@/stores';
import type { AddToCrmContactType } from '@/types/copilotContacts';
import type { CopilotFetchedAccount } from '@/types/sales';
import urls from '@/urls';

const {
  contact,
  recordId,
  account = null,
  variant = 'outline',
  addText = 'Add',
} = defineProps<{
  contact: AddToCrmContactType;
  recordId: string;
  account?: CopilotFetchedAccount | null;
  variant?: BittsButtonVariant;
  addText?: string;
}>();

const emit = defineEmits<{ added: [crmId: string] }>();

const route = useRoute();
const routeView = useRouteViewTracker((route?.name || '') as string);

const contactsStore = useCopilotContactsStore();
const flashesStore = useFlashesStore();

const { currentAuth } = useAuth();
const { iteratively } = useIteratively();
const { trackDealNavActivity } = useDealNavActivities();

const crmId = ref<string | null>(null);

const inOwnCrm = ref(contact.isInOwnCrm);
const savingToCRM = ref(false);

const showHover = computed(
  () => !!contactsStore.getCRMUrl && (!!contact.crmId || !!crmId.value),
);

const tooltipBinds = computed(() => {
  if (!showHover.value) {
    return { showTooltip: showHover.value };
  }
  return {};
});

const hasSalesforceFeed = inject(ProvidedHasSalesforceFeed);

const showAddToCrm = computed(
  () =>
    !!contact.lastName &&
    !!contact.email &&
    WRITE_ACCESS_ROLES.includes(currentAuth.value?.sales_edge_role || '') &&
    contactsStore.getContactsSources.length &&
    hasSalesforceFeed?.value,
);

const buttonText = computed(() => {
  if (!inOwnCrm.value && !savingToCRM.value) {
    return addText;
  } else if (savingToCRM.value) {
    return 'Saving...';
  }
  return 'Added';
});

const buttonDisabled = computed(() => savingToCRM.value || inOwnCrm.value);

const buttonIcon = computed(() => {
  if (inOwnCrm.value) {
    return ['fak', 'check'] as BittsIcon;
  } else if (!savingToCRM.value) {
    return ['fak', 'add'] as BittsIcon;
  }
  return undefined; // No icon when saving!
});

function navigateToContact(event: Event) {
  event.preventDefault();
  const targetCrmID = contact.crmId || crmId.value;
  if (contactsStore.getCRMUrl && targetCrmID) {
    const targetUrl = `${contactsStore.getCRMUrl}/${targetCrmID}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
}
type AddToCrmPayload = {
  first_name: string | null;
  last_name: string;
  email: string;
  phone: string | null;
  title: string | null;
};
function trackAddToCRM(payload: AddToCrmPayload) {
  iteratively.dealNavDrawerAction({
    type: 'user clicks add to CRM',
    metadata: {
      payload,
      account,
    },
    overlap_company_domain: account?.domain,
    overlap_company_name: account?.name,
    event_site: EVENT_SITES.DEAL_NAV_DRAWER_CONTACT_DETAIL,
    view: routeView,
  } as never);
  trackDealNavActivity('user_added_contact_to_crm', recordId, {
    contact_name: `${payload.first_name} ${payload.last_name}`,
    contact_email: payload.email,
  });
}

function addToCrmErrorMessage(error: unknown) {
  if (!axios.isAxiosError(error)) return 'An unknown error occurred';
  const data = error.response?.data;
  const errors = data?.errors;
  if (Array.isArray(errors) && errors.length) return errors[0];
  return errors?.message || data?.message || error.message;
}

async function addToCRM() {
  savingToCRM.value = true;
  const payload: AddToCrmPayload = {
    first_name: contact.firstName,
    last_name: contact.lastName,
    email: contact.email,
    phone: contact.phone,
    title: contact.title,
  };
  try {
    const response = await axios.post(
      urls.salesEdge.records.addToCrm(recordId),
      payload,
    );
    trackAddToCRM(payload);
    const { id: freshCRMId } = response.data;
    crmId.value = freshCRMId;
    inOwnCrm.value = true;
    emit('added', freshCRMId);
  } catch (error) {
    flashesStore.addErrorFlash({
      message: 'Failed to save to CRM',
      description: addToCrmErrorMessage(error),
    });
  } finally {
    savingToCRM.value = false;
  }
}
</script>
